import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { useToast } from '../../Context/ToastContext';
import { useConfirm } from '../../Context/ConfirmContext';

const IntegrationContainer = styled.div`
  background: rgba(99, 102, 241, 0.05);
  border: none;
  border-radius: 12px;
  border-left: 3px solid rgba(99, 102, 241, 0.5);
  padding: 24px;
  margin-bottom: 20px;
  backdrop-filter: blur(8px);
`;

const SectionTitle = styled.h2`
  color: #fff;
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 20px;
  padding: 10px 18px;
  display: inline-block;
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  letter-spacing: 0.3px;
`;

const IntegrationOption = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  margin-bottom: 12px;
  cursor: ${props => props.isAvailable ? 'pointer' : 'not-allowed'};
  transition: all 0.2s ease;
  background: ${props => props.isConnected 
    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)' 
    : 'rgba(255, 255, 255, 0.02)'};
  border-color: ${props => props.isConnected ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.2)'};
  opacity: ${props => props.isAvailable ? 1 : 0.6};
  backdrop-filter: blur(8px);

  &:hover {
    background: ${props => props.isAvailable ? 'rgba(99, 102, 241, 0.1)' : 'rgba(255, 255, 255, 0.02)'};
    border-color: ${props => props.isAvailable ? 'rgba(99, 102, 241, 0.4)' : 'rgba(99, 102, 241, 0.2)'};
    transform: ${props => props.isAvailable ? 'translateY(-1px)' : 'none'};
  }
`;

const IntegrationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IntegrationLogo = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background-color: ${props => props.bgColor || '#FFC62E'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 14px;
`;

// DocuSign Logo SVG
const DocuSignLogo = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15l-5-5 1.41-1.41L11 14.17l7.59-7.59L20 8l-9 9z" fill="#ffffff"/>
  </svg>
);

const IntegrationDetails = styled.div`
  flex: 1;
`;

const IntegrationName = styled.div`
  color: #FFFFFF;
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 4px;
`;

const IntegrationDescription = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${props => props.isConnected ? '#34A853' : 'rgba(255, 255, 255, 0.5)'};
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.isConnected ? '#34A853' : 'rgba(255, 255, 255, 0.3)'};
`;

const Button = styled.button`
  background: ${props => {
    if (props.variant === 'docusign') return 'linear-gradient(135deg, #FFC62E 0%, #E5A800 100%)';
    if (props.variant === 'danger') return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
    if (props.variant === 'primary') return 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)';
    return 'rgba(99, 102, 241, 0.15)';
  }};
  color: ${props => props.variant === 'docusign' ? '#000' : '#fff'};
  border: ${props => props.variant ? 'none' : '1px solid rgba(99, 102, 241, 0.3)'};
  border-radius: 8px;
  padding: 10px 18px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: ${props => props.variant ? '0 4px 12px rgba(99, 102, 241, 0.3)' : 'none'};
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${props => props.variant ? '0 6px 20px rgba(99, 102, 241, 0.4)' : 'none'};
    background: ${props => !props.variant ? 'rgba(99, 102, 241, 0.25)' : null};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const ConnectionSummary = styled.div`
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 12px;
  padding: 18px;
  margin-top: 16px;
  backdrop-filter: blur(8px);
`;

const SummaryTitle = styled.div`
  color: #fff;
  font-weight: 600;
  margin-bottom: 10px;
  font-size: 15px;
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
  margin-bottom: 6px;
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid ${props => props.dark ? '#000' : '#FFFFFF'};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(234, 67, 53, 0.1);
  border: 1px solid rgba(234, 67, 53, 0.3);
  border-radius: 8px;
  padding: 12px;
  color: #EA4335;
  font-size: 14px;
  margin-top: 12px;
`;

const SuccessMessage = styled.div`
  background-color: rgba(52, 168, 83, 0.1);
  border: 1px solid rgba(52, 168, 83, 0.3);
  border-radius: 8px;
  padding: 12px;
  color: #34A853;
  font-size: 14px;
  margin-top: 12px;
`;

const DocuSignIntegration = () => {
  const apiClient = useAuthenticatedAxios();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  
  const [connectionStatus, setConnectionStatus] = useState({
    connected: false,
    accountId: null,
    baseUri: null,
    lastConnectedAt: null
  });
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Check for OAuth callback result in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const docusignStatus = params.get('docusign');
    
    if (docusignStatus === 'connected') {
      setSuccess('DocuSign connected successfully!');
      // Remove the query params from URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Refresh status
      fetchConnectionStatus();
      // Clear success message after 5 seconds
      setTimeout(() => setSuccess(null), 5000);
    }
    
    if (docusignStatus === 'error') {
      const errorCode = params.get('error_code') || 'unknown';
      setError(`Failed to connect DocuSign (${errorCode}). Please try again.`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchConnectionStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/integrations/docusign/status');
      setConnectionStatus(response.data);
    } catch (err) {
      console.error('Error fetching DocuSign status:', err);
      // Don't show error for 404 (endpoint may not exist yet)
      if (err.response?.status !== 404) {
        setError('Failed to check DocuSign connection status');
      }
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  useEffect(() => {
    fetchConnectionStatus();
  }, [fetchConnectionStatus]);

  const handleConnect = async () => {
    try {
      setConnecting(true);
      setError(null);
      
      // Get the auth URL from the backend (authenticated request)
      const response = await apiClient.get('/integrations/docusign/connect');
      const { authUrl } = response.data;
      
      // Redirect to DocuSign OAuth page
      window.location.href = authUrl;
    } catch (err) {
      console.error('Error initiating DocuSign connection:', err);
      setError('Failed to start DocuSign connection. Please try again.');
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    const confirmed = await confirm({
      title: 'Disconnect DocuSign',
      message: 'Are you sure you want to disconnect DocuSign? You will need to reconnect to send documents for signature.',
      confirmText: 'Disconnect',
      cancelText: 'Cancel',
      type: 'warning'
    });
    
    if (!confirmed) return;

    try {
      setLoading(true);
      setError(null);
      await apiClient.post('/integrations/docusign/disconnect');
      setConnectionStatus({
        connected: false,
        accountId: null,
        baseUri: null,
        lastConnectedAt: null
      });
      showToast && showToast('DocuSign disconnected successfully', 'success');
    } catch (err) {
      console.error('Error disconnecting DocuSign:', err);
      setError('Failed to disconnect DocuSign');
    } finally {
      setLoading(false);
    }
  };

  return (
    <IntegrationContainer>
      <SectionTitle>E-Signature Integration</SectionTitle>

      <div style={{ marginBottom: '16px' }}>
        <p style={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '16px' }}>
          Connect your DocuSign account to send documents for electronic signatures directly from iMaven.
        </p>
      </div>

      {error && (
        <ErrorMessage>
          {error}
          <Button
            style={{ marginLeft: '12px', padding: '4px 8px', fontSize: '12px' }}
            onClick={() => setError(null)}
          >
            Dismiss
          </Button>
        </ErrorMessage>
      )}

      {success && (
        <SuccessMessage>
          {success}
        </SuccessMessage>
      )}

      {/* DocuSign Integration Card */}
      <IntegrationOption 
        isAvailable={true}
        isConnected={connectionStatus.connected}
        onClick={() => {
          if (!connectionStatus.connected && !connecting) {
            handleConnect();
          }
        }}
      >
        <IntegrationInfo>
          <IntegrationLogo bgColor="#FFC62E">
            <DocuSignLogo />
          </IntegrationLogo>
          <IntegrationDetails>
            <IntegrationName>DocuSign</IntegrationName>
            <IntegrationDescription>
              Send documents for electronic signatures
            </IntegrationDescription>
          </IntegrationDetails>
        </IntegrationInfo>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <StatusBadge isConnected={connectionStatus.connected}>
            <StatusDot isConnected={connectionStatus.connected} />
            {connectionStatus.connected ? 'Connected' : 'Available'}
          </StatusBadge>
          
          <div>
            {connectionStatus.connected ? (
              <Button 
                variant="danger" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleDisconnect();
                }}
                disabled={loading}
              >
                {loading ? <LoadingSpinner /> : 'Disconnect'}
              </Button>
            ) : (
              <Button 
                variant="docusign"
                onClick={(e) => {
                  e.stopPropagation();
                  handleConnect();
                }}
                disabled={connecting}
              >
                {connecting ? <LoadingSpinner dark /> : (
                  <>
                    <DocuSignLogo />
                    Connect
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </IntegrationOption>

      {/* Connection Summary (when connected) */}
      {connectionStatus.connected && (
        <ConnectionSummary>
          <SummaryTitle>DocuSign Connected</SummaryTitle>
          {connectionStatus.accountId && (
            <SummaryItem>
              <span>Account ID:</span>
              <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                {connectionStatus.accountId}
              </span>
            </SummaryItem>
          )}
          {connectionStatus.lastConnectedAt && (
            <SummaryItem>
              <span>Connected:</span>
              <span>{new Date(connectionStatus.lastConnectedAt).toLocaleString()}</span>
            </SummaryItem>
          )}
          <div style={{ marginTop: '12px', fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>
            You can now send documents for electronic signature from within iMaven.
          </div>
        </ConnectionSummary>
      )}

      {/* Other e-signature options (future) */}
      <IntegrationOption 
        isAvailable={false}
        isConnected={false}
        style={{ marginTop: '12px' }}
      >
        <IntegrationInfo>
          <IntegrationLogo bgColor="#0066CC">
            A
          </IntegrationLogo>
          <IntegrationDetails>
            <IntegrationName>Adobe Sign</IntegrationName>
            <IntegrationDescription>
              Coming Soon
            </IntegrationDescription>
          </IntegrationDetails>
        </IntegrationInfo>
        
        <StatusBadge isConnected={false}>
          <StatusDot isConnected={false} />
          Coming Soon
        </StatusBadge>
      </IntegrationOption>

      <IntegrationOption 
        isAvailable={false}
        isConnected={false}
      >
        <IntegrationInfo>
          <IntegrationLogo bgColor="#00B4E6">
            H
          </IntegrationLogo>
          <IntegrationDetails>
            <IntegrationName>HelloSign</IntegrationName>
            <IntegrationDescription>
              Coming Soon
            </IntegrationDescription>
          </IntegrationDetails>
        </IntegrationInfo>
        
        <StatusBadge isConnected={false}>
          <StatusDot isConnected={false} />
          Coming Soon
        </StatusBadge>
      </IntegrationOption>
    </IntegrationContainer>
  );
};

export default DocuSignIntegration;
